// ============================================================================
// INVOICE SERVICE (PMS Phase 2 slice 7a)
// ============================================================================
// crs.Api.Controllers.BillingModule.InvoicesController -> /api/v1/Invoices

import { get, post } from '../client/axiosClient';
import { buildEndpoint, buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse } from '../types';
import type { NotePreview, NoteRequest,
  Invoice,
  InvoiceListQuery,
  InvoiceListRow,
  InvoicePreview,
  IssueInvoicesResult,
  StayToInvoice,
} from '../types/invoice.types';

const BASE = '/Invoices';

const unwrap = <T,>(res: { data?: ApiResponse<T> }, fallback: string): T => {
  if (res.data?.data === undefined || res.data?.data === null) throw new Error(res.data?.message || fallback);
  return res.data.data;
};

class InvoiceService {
  async list(query: InvoiceListQuery): Promise<InvoiceListRow[]> {
    const q = Object.fromEntries(Object.entries(query).filter(([, v]) => v !== undefined && v !== ''));
    const res = await get<ApiResponse<InvoiceListRow[]>>(buildEndpointWithQuery(BASE, q as Record<string, string | number>));
    return res.data?.data ?? [];
  }

  async toInvoice(propertyId: string): Promise<StayToInvoice[]> {
    const res = await get<ApiResponse<StayToInvoice[]>>(buildEndpointWithQuery(`${BASE}/to-invoice`, { propertyId }));
    return res.data?.data ?? [];
  }

  async preview(bookingId: string): Promise<InvoicePreview> {
    return unwrap(await get<ApiResponse<InvoicePreview>>(buildEndpoint(`${BASE}/booking/${bookingId}/preview`)),
      'The invoice preview did not load.');
  }

  /** Posts the departed rooms' nights night audit has not posted yet (slice 7a-2); returns the new preview. */
  async postRemainingNights(bookingId: string): Promise<InvoicePreview> {
    return unwrap(await post<ApiResponse<InvoicePreview>>(buildEndpoint(`${BASE}/booking/${bookingId}/post-nights`), {}),
      'The remaining nights were not posted.');
  }

  async issue(bookingId: string): Promise<IssueInvoicesResult> {
    return unwrap(await post<ApiResponse<IssueInvoicesResult>>(buildEndpoint(`${BASE}/booking/${bookingId}/issue`), {}),
      'The invoices were not issued.');
  }

  /** What a credit (default) or debit note against the invoice would be (slice 7b). */
  async previewNote(id: string, request: NoteRequest): Promise<NotePreview> {
    return unwrap(await post<ApiResponse<NotePreview>>(buildEndpoint(`${BASE}/${id}/notes/preview`), request), 'The note preview failed.');
  }

  async issueNote(id: string, request: NoteRequest): Promise<Invoice> {
    return unwrap(await post<ApiResponse<Invoice>>(buildEndpoint(`${BASE}/${id}/notes`), request), 'The note was not issued.');
  }

  async get(id: string): Promise<Invoice> {
    return unwrap(await get<ApiResponse<Invoice>>(buildEndpoint(`${BASE}/${id}`)), 'The invoice did not load.');
  }

  /** The invoice PDF (open with showPdf). */
  async pdf(id: string): Promise<Blob> {
    const res = await get<Blob>(buildEndpoint(`${BASE}/${id}/pdf`), { responseType: 'blob' });
    return res.data;
  }

  /**
   * The guest statement PDF — a running folio before checkout, the final copy after. The
   * summary by default (a party's share one line, the booked package one line per room);
   * detailed = every folio line, night by night.
   */
  async statement(bookingId: string, detailed = false): Promise<Blob> {
    const res = await get<Blob>(buildEndpoint(`${BASE}/booking/${bookingId}/statement${detailed ? '?detailed=true' : ''}`), { responseType: 'blob' });
    return res.data;
  }
}

export const invoiceService = new InvoiceService();
export default invoiceService;
